import { Config } from '@retro-ui/config';
import { replace as replaceLocation } from 'connected-react-router';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useConfig } from '@f/hooks';
import { localStorageClient } from '@f/services/storage/wrapper';

import { SciFinderAuthContext } from './SciFinderAuthContext';
import { buildSSOUrl } from './helpers/buildSSOUrl';
import { buildStateAndCodeVerifier } from './helpers/buildStateAndCodeVerifier';
import { fetchToken } from './helpers/fetchToken';

type SciFinderAuthProviderProps = {
  children: React.ReactNode;
};

const STATE_KEY = 'scifinder_state';
const REDIRECT_URL_KEY = 'scifinder_redirect_url';
const CODE_VERIFIER_KEY = 'scifinder_code_verifier';
const ACCESS_TOKEN_KEY = 'scifinder_access_token';

const storage = localStorageClient;

export type AuthConfig = Config['global']['scifinder']['authentication'];

export const SciFinderAuthProvider = ({ children }: SciFinderAuthProviderProps) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const config = useConfig();

  const authConfig = config.scifinder.authentication;

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const searchParams = new URLSearchParams(location?.search);

  const redirectToUrl = (url: string) => {
    const finalRedirectUrl = new URL(url);
    finalRedirectUrl.searchParams.delete('code');
    finalRedirectUrl.searchParams.delete('state');

    dispatch(
      replaceLocation({
        pathname: finalRedirectUrl.pathname,
        search: finalRedirectUrl.search,
      })
    );
  };

  const process = async () => {
    const hasCode = searchParams.has('code');
    const hasState = searchParams.has('state');
    const redirectUrl = storage.getItem(REDIRECT_URL_KEY);

    if (!hasCode || !hasState || !redirectUrl) {
      return;
    }

    setIsAuthenticating(true);

    const state = storage.getItem(STATE_KEY) as string;
    const codeVerifier = storage.getItem(CODE_VERIFIER_KEY) as string;

    storage.removeItem(STATE_KEY);
    storage.removeItem(CODE_VERIFIER_KEY);
    storage.removeItem(REDIRECT_URL_KEY);

    try {
      const result = await fetchToken(state, codeVerifier, redirectUrl, authConfig);

      storage.setItem(ACCESS_TOKEN_KEY, result.access_token);
      setAccessToken(result.access_token);

      redirectToUrl(redirectUrl);
    } catch (error) {
      let message = 'Failed to authenticate in SciFinder.';

      if (error instanceof Error) {
        message = `${message}. ${error.message}`;
      }

      redirectToUrl(location.pathname);

      throw new Error(message);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const authenticate = async () => {
    if (searchParams.has('state')) {
      return;
    }

    setIsAuthenticating(true);

    const { state, codeVerifier } = buildStateAndCodeVerifier();
    const redirectUrl = window.location.href;

    storage.setItem(STATE_KEY, state);
    storage.setItem(CODE_VERIFIER_KEY, codeVerifier);
    storage.setItem(REDIRECT_URL_KEY, redirectUrl);

    const ssoUrl = await buildSSOUrl(state, codeVerifier, redirectUrl, authConfig);

    window.location.assign(ssoUrl);
  };

  useEffect(() => {
    const setTokenFromStorage = () => {
      const existingAccessToken = storage.getItem(ACCESS_TOKEN_KEY);

      if (existingAccessToken) {
        setAccessToken(existingAccessToken);
      }
    };

    setTokenFromStorage();
    window.addEventListener('storage', setTokenFromStorage);

    void process();

    return () => {
      window.removeEventListener('storage', setTokenFromStorage);
    };
  }, []);

  return (
    <SciFinderAuthContext.Provider
      value={{
        accessToken,
        isAuthenticating,
        authenticate,
      }}
    >
      {children}
    </SciFinderAuthContext.Provider>
  );
};
